/**
 * Payment-related constants
 * Centralized configuration for pricing, colors, and other payment-related values
 */

// Pricing constants
export const PAYMENT_CONSTANTS = {
  // Onboarding fee
  ONBOARDING_FEE: 12500,
  WHITE_GLOVE_ONBOARDING_FEE: 2500,

  // Subscription plan prices
  MONTHLY_PLAN_PRICE: 1100,
  SIX_MONTH_PLAN_PRICE: 6000,
} as const;

// Plan periods enum
export enum PlanPeriod {
  MONTHLY = "month",
  SIX_MONTHS = "6 months",
  PERMANENT = "one-time-payment",
}

// Plan periods
export const PLAN_PERIODS = {
  MONTHLY: PlanPeriod.MONTHLY,
  SIX_MONTHS: PlanPeriod.SIX_MONTHS,
  PERMANENT: PlanPeriod.PERMANENT,
} as const;

// Currency formatting utility
export const formatCurrency = (amount: number): string => {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

// Plan intervals enum
export enum PlanInterval {
  MONTHLY = "monthly",
  SIX_MONTHS = "six_months",
}

// Plan display names enum
export enum PlanDisplayName {
  MONTHLY = "Monthly Plan",
  SIX_MONTHS = "6-Month Plan",
}

// Plan period descriptions enum
export enum PlanPeriodDescription {
  MONTHLY = "per month",
  SIX_MONTHS = "per 6 months",
}

// Plan period descriptions
export const PLAN_PERIOD_DESCRIPTIONS = {
  MONTHLY: PlanPeriodDescription.MONTHLY,
  SIX_MONTHS: PlanPeriodDescription.SIX_MONTHS,
} as const;

// Legacy billing status constants - kept for backward compatibility
export const BILLING_STATUS = {
  REQUIRED: "REQUIRED",
  DONE: "DONE",
} as const;

// Map legacy billing status to new subscription status
export const mapSubscriptionStatusToBillingStatus = (
  subscriptionStatus?: string
): string => {
  switch (subscriptionStatus) {
    case SubscriptionStatus.ACTIVE:
      return BILLING_STATUS.DONE;
    case SubscriptionStatus.INACTIVE:
    case SubscriptionStatus.CANCELLED:
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.TRIAL:
    case SubscriptionStatus.INCOMPLETE:
    case SubscriptionStatus.INCOMPLETE_EXPIRED:
      return BILLING_STATUS.REQUIRED;
    default:
      return BILLING_STATUS.REQUIRED;
  }
};

// Legacy interface - kept for backward compatibility
export interface PaymentStatusResponse {
  billingStatus?: string;
  payment?: string;
  paymentType?: string;
  paymentProfile?: string;
  [key: string]: unknown; // Allow additional properties
}

// Subscription status enum to work with CurrentSubscriptionResponseDto
export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  CANCELLED = "cancelled",
  PAST_DUE = "past_due",
  TRIAL = "trial",
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
}

// Payment UI labels enum
export enum PaymentUILabels {
  PROCESSING = "Processing...",
  SELECT_PLAN = "Select a plan",
  SELECT_PLAN_FIRST = "Please select a plan first",
  PROCEED_TO_PAYMENT = "Proceed to Secure Payment",
  COMPLETE_ONBOARDING = "Complete Onboarding",
  SELECTED_PLAN_SUMMARY = "Selected Plan Summary",
  PAYMENT_COMPLETED = "Payment Completed Successfully!",
}

// Error messages enum
export enum PaymentErrorMessages {
  GENERIC_ERROR = "Unable to process payment. Please try again later.",
  ONBOARDING_ERROR = "Unable to complete onboarding. Please try again.",
}

export const PAYMENT_INFORMATION = [
  {
    title: "Standard Onboarding",
    description:
      "Covers secure account setup, clinical configuration, and integration into the Cellera medical network.",
    hasTooltip: true,
  },
  {
    title: "White Glove Onboarding",
    description:
      "Relieve administrative burden with this premium, hands-off service where our team captures, extracts, and structures your clinical data so you can focus entirely on patient care",
  },
  {
    title: "Monthly Subscription",
    description:
      "Provides ongoing access to the Cellera platform, AI-driven health insights, continuous system optimization, and access to the Cellera Institute.",
  },
];

export const ONBOARDING_FEE = [
  {
    title: "Standard Onboarding",
    price: formatCurrency(PAYMENT_CONSTANTS.ONBOARDING_FEE),
    readonly: true,
  },
  {
    title: "White Glove Onboarding",
    price: formatCurrency(PAYMENT_CONSTANTS.WHITE_GLOVE_ONBOARDING_FEE),
  },
];

export const PAYMENT_PLANS = {
  [PlanInterval.MONTHLY]: {
    id: PlanInterval.MONTHLY,
    title: PlanDisplayName.MONTHLY,
    price: formatCurrency(PAYMENT_CONSTANTS.MONTHLY_PLAN_PRICE),
    period: PLAN_PERIODS.MONTHLY,
  },
  [PlanInterval.SIX_MONTHS]: {
    id: PlanInterval.SIX_MONTHS,
    title: PlanDisplayName.SIX_MONTHS,
    price: formatCurrency(PAYMENT_CONSTANTS.SIX_MONTH_PLAN_PRICE),
    period: PLAN_PERIODS.SIX_MONTHS,
  },
};
